// ============================================================
// Moteur PUR de la frise du planning — AUCUNE dépendance Angular.
// Testé sans TestBed : hr-timeline.spec.ts.
// ============================================================
//
// Une pastille « 09:00–17:00 » ne dit rien de la DURÉE : deux créneaux empilés se ressemblent,
// qu'ils fassent une heure ou huit. On rend donc chaque créneau comme une BARRE dont la longueur
// est proportionnelle au temps de travail, posée sur une frise horaire.
//
// DEUX RÈGLES QUI COMPTENT :
//
//  1. L'ÉCHELLE EST COMMUNE À TOUTE LA GRILLE. Si chaque jour avait sa propre fenêtre, 9h du
//     lundi ne serait pas aligné avec 9h du mardi et la comparaison visuelle — le seul intérêt
//     de la chose — serait perdue. Les REPÈRES d'ouverture, eux, restent propres à chaque jour
//     (le samedi n'ouvre pas comme le mardi).
//
//  2. LA FENÊTRE S'ÉLARGIT AUX CRÉNEAUX. Un salarié qui reste après la fermeture doit se voir
//     dépasser du repère rouge, pas être tronqué. Même principe que l'éditeur d'horaires.

export interface TimelineWindow {
	start_min: number;
	end_min: number;
	span_min: number;
}

export interface TimelineBar {
	left_pct: number;		// position depuis le bord gauche de la frise
	width_pct: number;
}

// Repère d'ouverture ou de fermeture d'un service, avec son heure affichée au-dessus.
export interface TimelineMarker {
	min: number;
	kind: 'open' | 'close';
	label: string;			// « 9h », « 14h30 »
	left_pct: number;
}

/// Fenêtre COMMUNE de la grille : englobe les horaires d'ouverture ET tous les créneaux,
/// arrondie à l'heure pleine pour que les graduations tombent juste.
///
/// `fallback` sert quand il n'y a ni ouverture ni créneau (grille vide) — sinon la frise aurait
/// une largeur nulle et les barres disparaîtraient.
///
/// `roundMin` = pas d'arrondi des bords. À l'heure pleine par défaut (les graduations tombent
/// alors pile sur les bords de la frise) ; à la demi-heure pour l'éditeur d'horaires, qui trace
/// ses graduations lui-même et pour qui chaque heure de marge est de la place perdue.
export function timelineWindow(
	slots: { start_min: number; end_min: number }[],
	openings: { start_min: number; end_min: number }[],
	fallbackStart = 480,
	fallbackEnd = 1140,
	roundMin = 60,
): TimelineWindow {
	let lo = Number.POSITIVE_INFINITY;
	let hi = Number.NEGATIVE_INFINITY;

	for (const s of [...slots, ...openings]) {
		if (s.end_min <= s.start_min) continue;
		lo = Math.min(lo, s.start_min);
		hi = Math.max(hi, s.end_min);
	}
	// Grille vide : le repli est DÉJÀ une fenêtre d'heures pleines, on le rend tel quel plutôt
	// que de lui appliquer la marge (qui l'élargirait sans raison).
	if (!isFinite(lo) || !isFinite(hi)) {
		const span = Math.max(60, fallbackEnd - fallbackStart);
		return { start_min: fallbackStart, end_min: fallbackStart + span, span_min: span };
	}

	// Bords arrondis vers l'extérieur, avec une marge d'une demi-heure de chaque côté pour que les
	// barres ne collent jamais aux bords.
	const step = Math.max(1, roundMin);
	const start = Math.max(0, Math.floor((lo - 30) / step) * step);
	const end = Math.min(1440, Math.ceil((hi + 30) / step) * step);
	const span = Math.max(60, end - start);
	return { start_min: start, end_min: start + span, span_min: span };
}

/// Géométrie d'une barre dans la fenêtre, en pourcentages (le conteneur porte la largeur réelle).
/// Un créneau qui déborde de la fenêtre est BORNÉ plutôt que masqué : mieux vaut une barre
/// tronquée qu'un créneau invisible.
export function barGeometry(start_min: number, end_min: number, win: TimelineWindow): TimelineBar {
	const lo = Math.max(start_min, win.start_min);
	const hi = Math.min(end_min, win.end_min);
	if (hi <= lo) return { left_pct: 0, width_pct: 0 };
	return {
		left_pct: ((lo - win.start_min) / win.span_min) * 100,
		width_pct: ((hi - lo) / win.span_min) * 100,
	};
}

/// Repères d'ouverture (vert) et de fermeture (rouge) d'une journée, positionnés sur la frise.
/// Une journée à deux services (matin / après-midi) donne quatre repères : 9h · 12h · 14h · 19h.
export function openingMarkers(
	openings: { start_min: number; end_min: number }[],
	win: TimelineWindow,
): TimelineMarker[] {
	const out: TimelineMarker[] = [];
	for (const o of openings) {
		if (o.end_min <= o.start_min) continue;
		out.push({ min: o.start_min, kind: 'open', label: hourLabel(o.start_min), left_pct: pct(o.start_min, win) });
		out.push({ min: o.end_min, kind: 'close', label: hourLabel(o.end_min), left_pct: pct(o.end_min, win) });
	}
	// Deux services qui s'enchaînent (12h fermeture / 12h réouverture) ne doivent pas empiler
	// deux étiquettes au même endroit.
	const seen = new Set<string>();
	return out
		.filter(m => {
			const key = m.min + '|' + m.kind;
			if (seen.has(key)) return false;
			seen.add(key);
			return true;
		})
		.sort((a, b) => a.min - b.min);
}

/// Plages d'ouverture rendues en fond de cellule, pour que le hors-ouverture se distingue.
export function openingBands(
	openings: { start_min: number; end_min: number }[],
	win: TimelineWindow,
): TimelineBar[] {
	return openings
		.filter(o => o.end_min > o.start_min)
		.map(o => barGeometry(o.start_min, o.end_min, win))
		.filter(b => b.width_pct > 0);
}

/// Graduations horaires de la frise — toutes les `stepMin` minutes, sur les heures pleines.
export function timelineTicks(win: TimelineWindow, stepMin = 60): { min: number; label: string; left_pct: number }[] {
	const out: { min: number; label: string; left_pct: number }[] = [];
	for (let m = win.start_min; m <= win.end_min; m += stepMin) {
		out.push({ min: m, label: hourLabel(m), left_pct: pct(m, win) });
	}
	return out;
}

function pct(min: number, win: TimelineWindow): number {
	return Math.min(100, Math.max(0, ((min - win.start_min) / win.span_min) * 100));
}

/// Fusionne des plages qui se chevauchent ou se touchent. Deux services ouverts en même temps
/// ne doivent pas afficher deux fois le même repère d'ouverture.
export function mergeRanges(ranges: { start_min: number; end_min: number }[]): { start_min: number; end_min: number }[] {
	const sorted = ranges
		.filter(r => r.end_min > r.start_min)
		.slice()
		.sort((a, b) => a.start_min - b.start_min);

	const out: { start_min: number; end_min: number }[] = [];
	for (const r of sorted) {
		const last = out[out.length - 1];
		if (last && r.start_min <= last.end_min) {
			last.end_min = Math.max(last.end_min, r.end_min);
		} else {
			out.push({ start_min: r.start_min, end_min: r.end_min });
		}
	}
	return out;
}

/// « 9h », « 14h30 » — l'heure telle qu'on la dit, pas « 09:00 ».
export function hourLabel(min: number): string {
	const h = Math.floor(min / 60);
	const m = min % 60;
	return h + 'h' + (m > 0 ? (m < 10 ? '0' + m : '' + m) : '');
}
